import {HttpClient} from '@angular/common/http'
import {inject, Service} from '@angular/core'
import {APPLICATION_ID, IApplicationSummary, IRegistration, UpdateValues} from '@sparbanken-syd/loan-backend'
import {BehaviorSubject, throwError} from 'rxjs'
import {catchError, tap} from 'rxjs/operators'
import {environment} from '../../environments/environment'

interface Url {
  url: string
}

@Service()
export class RegisterService {
  public registeredPromises$ = new BehaviorSubject<IRegistration[]>([])
  private readonly http = inject(HttpClient)

  public saveRegistered(registeredLoanPromise: IRegistration) {
    const url = `${environment.loanServiceUrl}/register`
    return this.http.put<IRegistration>(url, registeredLoanPromise).pipe(
      tap((newRegistration) => {
        this.updateRegisteredList([...this.registeredPromises$.value, newRegistration])
      }),
      catchError(this.handleError)
    )
  }

  public updateValues(id: string, data: UpdateValues) {
    const url = `${environment.loanServiceUrl}/update-values/${id}`
    return this.http.put<IApplicationSummary>(url, data)
  }

  public getRegistrations() {
    const url = `${environment.loanServiceUrl}/register`
    return this.http.get<IRegistration[]>(url).pipe(
      tap((registrations) => {
        this.registeredPromises$.next(registrations)
      }),
      catchError(this.handleError)
    )
  }

  public getRegistered(id: string) {
    const url = `${environment.loanServiceUrl}/register/${id}`
    return this.http.get<IRegistration>(url).pipe(
      catchError(this.handleError)
    )
  }

  public delete(id: string) {
    const url = `${environment.loanServiceUrl}/register/${id}`
    return this.http.delete<void>(url).pipe(
      tap(() => {
        this.updateRegisteredList(
          this.registeredPromises$.value.filter(lp => lp[APPLICATION_ID] !== id)
        )
      }),
      catchError(this.handleError)
    )
  }

  public updateRegistered(registeredLoanPromise: IRegistration) {
    const url = `${environment.loanServiceUrl}/register/${registeredLoanPromise[APPLICATION_ID]}`

    return this.http.put<IRegistration>(url, registeredLoanPromise).pipe(
      tap((updatedRegistration) => {
        this.updateRegisteredList(
          this.registeredPromises$.value.map(r =>
            r[APPLICATION_ID] === updatedRegistration[APPLICATION_ID] ? updatedRegistration : r
          )
        )
      }),
      catchError(this.handleError)
    )
  }

  public getLoanDocument(application: IApplicationSummary) {
    const registeredUrl = `${environment.loanServiceUrl}/register/print/${application[APPLICATION_ID]}`
    const documentsUrl = `${environment.loanServiceUrl}/documents/${application[APPLICATION_ID]}`

    return application.status === 'registered'
      ? this.http.put<Url>(registeredUrl, {}).pipe(catchError(this.handleError))
      : this.http.get<Url>(documentsUrl).pipe(catchError(this.handleError))
  }

  // Utility function to update the registeredPromises$ with sorted registrations
  private updateRegisteredList(updatedList: IRegistration[]) {
    this.registeredPromises$.next(updatedList
      .sort((a, b) => b.dateOfIssue - a.dateOfIssue))
  }

  // Utility function to handle error logging or management
  private handleError(error: any) {
    console.error('Error occurred:', error)
    return throwError(() => new Error('Something went wrong with the API request.'))
  }
}
